import type { LucideIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MANAGER_QUICK_ACTION_ITEMS } from "@/constants/navigation";
import { cn } from "@/lib/utils";

export function ManagerQuickActions() {
  if (!MANAGER_QUICK_ACTION_ITEMS.length) {
    return null;
  }

  return (
    <div className="relative">
      <nav
        className="flex flex-nowrap gap-3 overflow-x-auto border-b border-border/60 bg-background/88 px-4 py-3 shadow-[var(--surface-shadow-sm)] backdrop-blur scrollbar-hidden lg:mx-auto lg:grid lg:max-w-[90rem] lg:grid-cols-5 lg:overflow-visible lg:px-8"
        aria-label="Quick manager navigation"
      >
        {MANAGER_QUICK_ACTION_ITEMS.map(
          ({ to, shortLabel, image: imageUrl, icon }, idx) => (
            <Link
              key={to}
              to={to}
              className={cn(
                "flex h-24 min-w-[5.75rem] shrink-0 flex-col items-center justify-center gap-2 rounded-lg border border-border/60 bg-card/92 p-3 text-foreground shadow-[var(--surface-shadow-sm)] transition-colors hover:bg-muted/45 lg:h-auto lg:min-w-0 lg:gap-3",
                {
                  "mr-[10%] lg:mr-0":
                    idx === MANAGER_QUICK_ACTION_ITEMS.length - 1,
                }
              )}
            >
              <QuickActionMedia imageUrl={imageUrl} icon={icon} />
              <span className="text-center text-xs font-semibold tracking-wide text-foreground">
                {shortLabel}
              </span>
            </Link>
          )
        )}
      </nav>
      <div className="pointer-events-none bg-gradient-to-l from-transparent to-background absolute top-0 left-0 h-full w-[10%] md:hidden" />
      <div className="pointer-events-none bg-gradient-to-r from-transparent to-background absolute top-0 right-0 h-full w-[20%] md:hidden" />
    </div>
  );
}

function QuickActionMedia({
  imageUrl,
  icon: Icon,
}: {
  imageUrl?: string;
  icon: LucideIcon;
}) {
  const [imageFailed, setImageFailed] = useState(false);

  if (imageUrl && !imageFailed) {
    return (
      <span className="flex size-12 shrink-0 items-center justify-center lg:size-16">
        <img
          src={imageUrl}
          alt=""
          className="max-h-full max-w-full object-contain"
          loading="eager"
          onError={() => setImageFailed(true)}
        />
      </span>
    );
  }

  return (
    <span className="flex size-12 shrink-0 items-center justify-center rounded-md bg-accent text-foreground lg:size-16">
      <Icon className="size-5 lg:size-7" aria-hidden />
    </span>
  );
}
